import {
  resolveBlogCover,
  resolveBlogInline,
} from './mediaHelpers';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const normalizeTags = (tags) => {
  if (Array.isArray(tags)) return tags.map((tag) => String(tag).trim()).filter(Boolean);
  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
};

export const mapBlogListItem = (blog) => ({
  id: blog.documentId,
  title: blog.title,
  author: blog.author || 'Q-Tech',
  date: formatDate(blog.publishedDate),
  category: blog.category,
  excerpt: blog.excerpt,
  image: resolveBlogCover(blog),
  slug: blog.slug,
  link: `/blog/${blog.slug}`,
  tags: normalizeTags(blog.tags),
});

export const mapBlogDetail = (blog) => ({
  title: blog.title,
  author: blog.author || 'Q-Tech',
  date: formatDate(blog.publishedDate),
  category: blog.category,
  excerpt: blog.excerpt,
  image: resolveBlogCover(blog),
  inlineImage: resolveBlogInline(blog),
  body: blog.body || blog.excerpt,
  slug: blog.slug,
  link: `/blog/${blog.slug}`,
  tags: normalizeTags(blog.tags),
});

export const bodyParagraphs = (body) =>
  (body || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

export const bodyBulletLines = (body) =>
  (body || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('- '))
    .map((line) => line.replace(/^- /, ''));

export const parseBlogBody = (body) => {
  const blocks = [];
  const lines = (body || '').split('\n');
  let paragraphBuffer = [];
  let listBuffer = [];

  const flushParagraph = () => {
    const text = paragraphBuffer.join(' ').trim();
    if (!text) {
      paragraphBuffer = [];
      return;
    }
    if (text.length < 120 && !text.includes('\n') && !text.endsWith('.')) {
      blocks.push({ type: 'heading', text, className: '' });
    } else {
      blocks.push({ type: 'paragraph', text });
    }
    paragraphBuffer = [];
  };

  const flushList = () => {
    if (!listBuffer.length) return;
    blocks.push({ type: 'list', items: [...listBuffer] });
    listBuffer = [];
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      flushParagraph();
      return;
    }

    if (trimmed.startsWith('- ')) {
      flushParagraph();
      listBuffer.push(trimmed.replace(/^- /, ''));
      return;
    }

    if (trimmed.startsWith('> ')) {
      flushList();
      flushParagraph();
      blocks.push({ type: 'blockquote', text: trimmed.slice(2) });
      return;
    }

    if (trimmed.startsWith('## ')) {
      flushList();
      flushParagraph();
      blocks.push({ type: 'heading', text: trimmed.slice(3), className: '' });
      return;
    }

    if (trimmed.startsWith('### ')) {
      flushList();
      flushParagraph();
      blocks.push({ type: 'heading', text: trimmed.slice(4), className: 'title2' });
      return;
    }

    if (listBuffer.length) flushList();
    paragraphBuffer.push(trimmed);
  });

  flushList();
  flushParagraph();

  return blocks;
};

export const insertInlineImageBlock = (blocks, imageUrl, alt) => {
  if (!imageUrl) return blocks;

  const listIndex = blocks.findIndex((block) => block.type === 'list');
  const insertAt = listIndex >= 0 ? listIndex + 1 : Math.min(2, blocks.length);
  const next = [...blocks];
  next.splice(insertAt, 0, { type: 'image', src: imageUrl, alt });
  return next;
};
